import React from "react";

import { cn, PageHeader, Paragraph } from "@skydio/rivet-ui";

import type { TagType } from "antd";

export interface SettingsPageViewProps {
  title: string | React.ReactNode;
  subTitle?: string | React.ReactNode;
  link?: {
    text: string;
    url: string;
  };
  tags?: React.ReactElement<TagType> | React.ReactElement<TagType>[];
  icon?: React.ReactNode;
  extra?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  children?: React.ReactNode;
  withDivider?: boolean;
  contentClassName?: string;
}

/**
 * A page view for settings pages that has a one line header with a title, subtitle, tags, and extra content.
 */
const SettingsPageView: React.FC<SettingsPageViewProps> = ({
  title,
  subTitle,
  link,
  tags,
  extra,
  icon,
  withDivider = true,
  breadcrumbs,
  children,
  contentClassName = "",
}) => {
  return (
    <>
      <PageHeader withDivider={withDivider}>
        {breadcrumbs && breadcrumbs}
        <PageHeader.Heading>
          <PageHeader.Heading.Main>
            <PageHeader.Heading.Title>{title}</PageHeader.Heading.Title>
            {subTitle && <PageHeader.Heading.Subtitle>{subTitle}</PageHeader.Heading.Subtitle>}
            {link && (
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm">
                {link.text}
              </a>
            )}
            {tags && <span>{tags}</span>}
          </PageHeader.Heading.Main>
          {extra && <PageHeader.Heading.Extra>{extra}</PageHeader.Heading.Extra>}
        </PageHeader.Heading>
      </PageHeader>
      <div className={cn("flex flex-col gap-6 px-8 pb-8", withDivider && "pt-4", contentClassName)}>
        {children}
      </div>
    </>
  );
};

/**
 * A variant of SettingsPageView that has a larger header, useful for pages that have a lot of header content
 * - The first line contains the title and the extra content
 * - The second line contains the subtitle and link
 * - The third line contains the tags
 */
export const SettingsPageViewBigHeader: React.FC<SettingsPageViewProps> = ({
  title,
  subTitle,
  link,
  tags,
  extra,
  icon,
  withDivider = true,
  breadcrumbs,
  children,
}) => {
  return (
    <>
      <PageHeader withDivider={withDivider}>
        {breadcrumbs && breadcrumbs}
        <PageHeader.Heading>
          <PageHeader.Heading.Title>{title}</PageHeader.Heading.Title>
          {extra && <PageHeader.Heading.Extra>{extra}</PageHeader.Heading.Extra>}
        </PageHeader.Heading>
        <PageHeader.Content>
          <Paragraph emphasis="secondary">
            {subTitle && <span>{subTitle} </span>}
            {link && (
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm">
                {link.text}
              </a>
            )}
            {tags && <span>{tags}</span>}
          </Paragraph>
        </PageHeader.Content>
      </PageHeader>
      <div className={cn("flex flex-col gap-6 px-8 pb-8", withDivider && "pt-4")}>{children}</div>
    </>
  );
};

export default SettingsPageView;
